import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Currency = 'EUR' | 'USD' | 'TRY';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  convertPrice: (price: number, fromCurrency: string) => number;
  formatPrice: (price: number, fromCurrency?: string) => string;
  exchangeRates: Record<string, number>;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  EUR: '€',
  USD: '$',
  TRY: '₺'
};

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState<Currency>(() => {
    const saved = localStorage.getItem('preferred_currency');
    return (saved as Currency) || 'EUR';
  });

  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({
    EUR: 1,
    USD: 1.09,
    TRY: 37.5,
    GBP: 0.85
  });

  useEffect(() => {
    // Fetch real-time exchange rates
    const fetchRates = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/EUR');
        const data = await response.json();
        setExchangeRates({
          EUR: 1,
          USD: data.rates.USD || 1.09,
          TRY: data.rates.TRY || 37.5,
          GBP: data.rates.GBP || 0.85
        });
      } catch (error) {
        console.error('Failed to fetch exchange rates:', error);
        // Keep default rates if fetch fails
      }
    };

    fetchRates();
    // Update rates every hour
    const interval = setInterval(fetchRates, 3600000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem('preferred_currency', currency);
  }, [currency]);

  const convertPrice = (price: number, fromCurrency: string = 'EUR'): number => {
    // Convert from source currency to EUR first
    const priceInEUR = price / (exchangeRates[fromCurrency] || 1);
    // Then convert from EUR to target currency
    return priceInEUR * exchangeRates[currency];
  };

  const formatPrice = (price: number, fromCurrency: string = 'EUR'): string => {
    const convertedPrice = convertPrice(price, fromCurrency);
    const symbol = CURRENCY_SYMBOLS[currency];
    return `${symbol}${convertedPrice.toFixed(2)}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convertPrice, formatPrice, exchangeRates }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
}
